import React from 'react';
import Grid2 from '@mui/material/Unstable_Grid2';
import { Card, CardContent, Typography, Box } from '@mui/material';
import BarChartIcon from '@mui/icons-material/BarChart';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';

const PeerComparisonCharts = ({ data }) => {
  // Ensure data is an array
  const peerData = Array.isArray(data) ? data : [];

  if (!peerData || peerData.length === 0) {
    return (
      <Card>
        <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="subtitle2">
            Peer Comparison Analytics
          </Typography>
        </Box>
        <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 200 }}>
          <Box sx={{ textAlign: 'center', color: 'text.secondary' }}>
            <BarChartIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography variant="body2">No peer comparison data available</Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  // Sort by outlier count descending
  const sortedData = [...peerData].sort((a, b) =>
    (b.outlier_count || 0) - (a.outlier_count || 0)
  );

  // Top 10 groups for bar chart
  const barChartData = sortedData.slice(0, 10).map(item => ({
    group: item.group || 'Unknown',
    avgGroups: parseFloat(item.avg_groups || 0).toFixed(1),
    peerAvg: parseFloat(item.peer_avg_groups || 0).toFixed(1),
    avgExcess: parseFloat(item.avg_excess_groups || 0).toFixed(1),
    outliers: item.outlier_count || 0
  }));

  // Calculate outlier distribution for pie chart
  const totalUsers = peerData.reduce((sum, dept) => sum + (dept.total_users || 0), 0);
  const totalOutliers = peerData.reduce((sum, dept) => sum + (dept.outlier_count || 0), 0);
  const normalUsers = totalUsers - totalOutliers;

  const pieChartData = [
    { name: 'Normal Access', value: normalUsers, color: '#5da87a' },
    { name: 'Excessive Access (5+ above peer avg)', value: totalOutliers, color: '#d27373' }
  ];

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <Box
          sx={{
            bgcolor: 'background.paper',
            p: 1.5,
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
          }}
        >
          <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 'bold' }}>{payload[0].payload.group}</Typography>
          {payload.map((entry, index) => (
            <Typography key={index} variant="caption" sx={{ color: entry.color, display: 'block', mb: 0.5 }}>
              <strong>{entry.name}:</strong> {entry.value}
            </Typography>
          ))}
        </Box>
      );
    }
    return null;
  };

  const PieTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const pieData = payload[0];
      const percentage = ((pieData.value / totalUsers) * 100).toFixed(1);
      return (
        <Box
          sx={{
            bgcolor: 'background.paper',
            p: 1.5,
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
          }}
        >
          <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 'bold' }}>{pieData.name}</Typography>
          <Typography variant="body2">
            <strong>{pieData.value}</strong> users ({percentage}%)
          </Typography>
        </Box>
      );
    }
    return null;
  };

  return (
    <Card>
      <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="subtitle2">
          Peer Comparison Analytics
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          Comparing user access patterns by group membership
        </Typography>
      </Box>
      <CardContent sx={{ p: 2, pt: 1 }}>
        <Grid2 container spacing={2}>
          {/* Bar Chart - Excessive Access by Group */}
          <Grid2 xs={12} lg={8}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              Excessive Access by Group
            </Typography>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart
                data={barChartData}
                margin={{ top: 5, right: 20, left: 10, bottom: 60 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                <XAxis
                  dataKey="group"
                  angle={-45}
                  textAnchor="end"
                  height={60}
                  tick={{ fill: '#94a3b8', fontSize: 10 }}
                />
                <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} />
                <Tooltip content={<CustomTooltip />} />
                <Legend wrapperStyle={{ fontSize: '0.75rem' }} />
                <Bar dataKey="avgGroups" name="Avg Groups" fill="#2563EB" />
                <Bar dataKey="peerAvg" name="Peer Average" fill="#5da87a" />
                <Bar dataKey="avgExcess" name="Avg Excess" fill="#d27373" />
              </BarChart>
            </ResponsiveContainer>
          </Grid2>

          {/* Pie Chart - Outlier Distribution */}
          <Grid2 xs={12} lg={4}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              Access Distribution
            </Typography>
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie
                  data={pieChartData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {pieChartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip content={<PieTooltip />} />
                <Legend wrapperStyle={{ fontSize: '0.7rem' }} />
              </PieChart>
            </ResponsiveContainer>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary">
                {totalOutliers} of {totalUsers} users ({((totalOutliers/totalUsers)*100).toFixed(1)}%)
                have excessive access
              </Typography>
            </Box>
          </Grid2>
        </Grid2>
      </CardContent>
    </Card>
  );
};

export default PeerComparisonCharts;
